import React from "react"
import { useTranslation } from "react-i18next"

import { Button, Spinner } from "components/Elements"
import { FormElement, formStatusEnum } from "components/Form"

import * as BikeApi from "features/bike/api/BikeApi"

import { useAlert, useForm } from "hooks"
import { ErrorResponse } from "models"
import { BikePageProps } from "../../models"
import { jsonHash } from "utils"

export const BikePrice: React.FC<BikePageProps> = ({ bike, refreshBike }) => {
	const {
		formFields,
		formStatus,
		setFormStatus,
		handleChange,
		errors,
		showLocalErrors,
		setShowLocalErrors,
		handleValidation,
		discardChanges,
	} = useForm({
		defaultFormFields: bike,
		defaultFormStatus: formStatusEnum.readonly,
	})

	const { t } = useTranslation()
	const { alert } = useAlert()

	const isLoading = formStatus === formStatusEnum.loading
	const isReadonly = formStatus === formStatusEnum.readonly
	const isReady = formStatus === formStatusEnum.ready

	const formHasChanged = jsonHash(bike) !== jsonHash(formFields)

	const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			try {
				// hide error
				setShowLocalErrors(false)
				// prevent form to be submitted again
				setFormStatus(formStatusEnum.loading)

				const operatorData = formFields.operator_data

				const newOperatorData = {
					...operatorData,
					bike_price: operatorData.bike_price,
				}

				const updatedBikePayload = {
					bike_id: bike.bike_id,
					row_version: bike.row_version,
					operator_data: newOperatorData,
				}

				const { getError } = await BikeApi.updateBike(updatedBikePayload)
				if (getError()) throw getError()

				alert("success", t("info_update", { ns: "form" }))
				await refreshBike()
			} catch (error) {
				console.error(error)
				const errorMsg = (error as ErrorResponse)?.message?.toString()
				alert("error", errorMsg)
			} finally {
				setFormStatus(formStatusEnum.ready)
			}
		}
	}

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleChange,
	}

	const formatPrice = (price: number, country = "fr-FR", currency = "EUR") => {
		if (!price) return undefined

		const formatter = new Intl.NumberFormat(country, {
			style: "currency",
			currency: currency,
		})

		return formatter.format(price)
	}

	return (
		<form>
			{isReady && (
				<>
					<FormElement
						{...formElementCommonProps}
						value={formFields.operator_data?.bike_price}
						name="operator_data.bike_price"
						type="number"
						label={t("description.price", { ns: "bike" })}
						className="my-3 px-1"
					/>

					<div className="flex items-center justify-center">
						<Button
							variant="secondary"
							type="submit"
							onClick={handleSubmit}
							className="mx-1 px-10 py-1 lg:mx-2"
							size="small"
							disabled={!formHasChanged}
						>
							{t("global.validate")}
						</Button>

						<Button
							variant="secondaryBordered"
							type="button"
							onClick={() =>
								discardChanges({ formHasChanged, refreshData: refreshBike })
							}
							className="mx-1 px-10 py-1 lg:mx-2"
							size="small"
						>
							{t("global.cancel")}
						</Button>
					</div>
				</>
			)}

			{isReadonly && (
				<>
					<FormElement
						value={formatPrice(formFields.operator_data?.bike_price)}
						name="operator_data.bike_price"
						type="text"
						label={t("description.price", { ns: "bike" })}
						className="my-3 px-1"
						readonly={true}
					/>

					<div className="flex justify-center">
						<Button
							variant="primary"
							type="submit"
							onClick={() => setFormStatus(formStatusEnum.ready)}
							className="px-10 py-1 mx-3"
							size="small"
						>
							{t("global.edit")}
						</Button>
					</div>
				</>
			)}

			{isLoading && (
				<div className="flex justify-center my-8">
					<Spinner color={"var(--primaryColor)"} size={"100px"} />
				</div>
			)}
		</form>
	)
}
